<template>
  <MultiStepDialog
    ref="dialogRef"
    :steps="steps"
    background="white"
    size="bulk-actions"
    :showFooter="showFooter"
    :confirmLabel="$t('components.buttons.save')"
    :disableConfirm="!componentRef?.isDirty"
    :closeOnCancel="false"
    @cancel="dialogRef?.handleStepBack()"
    @confirm="componentRef?.submit()"
    @clear="componentRef?.clear()"
  >
    <slot />
    <template
      v-if="steps[dialogRef?.currentStep]?.search && componentRef?.selected"
      #search="{ step, searchProps }"
    >
      <Component
        :is="step.search"
        :key="componentRef.selected.length"
        v-model="componentRef.selected"
        :buildingIds="buildingIds"
        v-bind="searchProps"
      />
    </template>
    <template #content="{ step, props, handleGoToStep }">
      <div v-if="!steps[dialogRef?.currentStep]?.withSearch" class="h-5" />
      <Component
        ref="componentRef"
        :is="step.content"
        v-bind="props"
        :buildingIds="buildingIds"
        :query="dialogRef?.query"
        @query:clear="dialogRef?.clearQuery()"
        :class="
          cn(
            'grow py-3 px-6 bg-secondary border-t border-secondary overflow-y-auto',
            props.class
          )
        "
        @goToStep="handleGoToStep"
        @submit="handleSubmit"
      />
    </template>
    <template v-if="showNotice" #footer>
      <div class="flex py-5 px-6 gap-3 border-t border-secondary">
        <FeaturedIcon
          name="error-circle"
          color="brand"
          type="dark"
          size="xs"
          radius="rounded"
          class="shrink-0"
        />
        <div class="flex flex-col grow gap-1">
          <span class="text-secondary text-sm leading-base font-semibold">
            {{ $t("components.bulk_actions.notice.label") }}
          </span>
          <ul
            class="list-disc ltr:ml-3.5 rtl:mr-3.5 text-tertiary text-style-xs font-normal"
          >
            <li
              v-for="(hint, index) in hints"
              :key="index"
              class="text-tertiary text-style-xs font-normal"
            >
              {{ hint }}
            </li>
          </ul>
        </div>
      </div>
    </template>
  </MultiStepDialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { ActionType } from "@/SharedComponents/BulkActions";
import { MultiStepDialog } from "@/SharedComponents/Dialogs";
import { FeaturedIcon } from "@/SharedComponents/FeaturedIcon";
import { Steps } from "@/Composables/useSteps";
import { includeHours } from "@/Utils/time";
import { toUTC } from "@/Utils/date";
import { cn } from "@/Utils/cn";

const dialogRef = ref(null);
const componentRef = ref(null);

const emit = defineEmits(["submit"]);

const props = defineProps({
  buildingIds: { type: Array, required: false },
  steps: { type: Steps, default: () => [] },
  hints: { type: Array, required: false },
});

const showFooter = computed(
  () => dialogRef.value?.currentStep !== "list" || props.hints?.length > 0
);

const showNotice = computed(
  () => dialogRef.value?.currentStep === "list" && props.hints?.length > 0
);

function parseData(type, data) {
  switch (type) {
    case ActionType.DUE_DATE:
      return {
        scheduled_for: data ? toUTC(includeHours(data).toISOString()) : null,
      };
    case ActionType.EXPIRATION_DATE:
      return {
        expiration_date: data ? toUTC(includeHours(data).toISOString()) : null,
      };
    case ActionType.NOTIFICATION_DATE:
      return {
        reminder_date: data ? toUTC(includeHours(data).toISOString()) : null,
      };
    case ActionType.PRIORITY:
      return {
        priority_id: data,
      };
    case ActionType.CATEGORY:
      return {
        category_ids: data
          ? props.buildingIds?.length === 1
            ? [data.id]
            : data.ids
          : [],
      };
    case ActionType.ASSIGNED_TO:
      return data.reduce(
        (acc, user) => {
          if (user.checked === false) {
            acc.user_ids_to_remove.push(user.id);
          } else if (user.checked !== "indeterminate") {
            acc.user_ids_to_add.push(user.id);
          }
          return acc;
        },
        { user_ids_to_add: [], user_ids_to_remove: [] }
      );
    case ActionType.TAGS:
      return data.reduce(
        (acc, tag) => {
          if (tag.checked === false) {
            acc.tag_ids_to_remove.push(tag.id);
          } else if (tag.checked !== "indeterminate") {
            acc.tag_ids_to_add.push(tag.id);
          }
          return acc;
        },
        { tag_ids_to_add: [], tag_ids_to_remove: [] }
      );
    case ActionType.VISIBILITY:
      return {
        is_private: data === "private",
      };
    case ActionType.ASSET_GROUPS:
      return {
        group_id: data.id,
      };
    default:
      return {};
  }
}

function handleSubmit(data, type = dialogRef.value?.currentStep) {
  emit("submit", {
    type,
    payload: parseData(type, data),
  });
}

watch(
  () => dialogRef?.currentStep,
  () => {
    dialogRef.value?.clearQuery();
  }
);
</script>
